<template>
  <figure class="pdl-swatch relative rounded-full bg-white" :class="swatchClasses">
    <img v-if="imageUrl" class="rounded-full absolute will-change-transform" :src="imageUrl" alt="" />
    <div
      v-else
      :style="`background-color: var(--pdl-swatch-${firstColor});`"
      class="rounded-full absolute overflow-hidden"
    >
      <div
        v-if="secondColor"
        :style="`background-color: var(--pdl-swatch-${secondColor});`"
        class="absolute bottom-0 w-full h-1/2"
      />
    </div>
    <figcaption class="sr-only">{{ figcaptionText }} {{ !isAvailable ? `, ${unavailableLabel}` : '' }}</figcaption>
  </figure>
</template>

<script>
export default {
  name: 'PdlSwatch',

  props: {
    /**
     * This prop is required when an imageUrl is used. It's optional otherwise.
     *
     * It must be a string without spaces or special characters (i.e. 'blue', 'black_teal').
     * If provided it will be used as the plain text name for the
     * aria label, qaid, input id, etc.
     *
     * If not provided then other strings will be used for this purpose following this priority order:
     *
     * name -> firstcolor_secondcolor -> firstcolor -> Option index
     */
    name: {
      type: String,
      default: null,
    },

    /**
     * The translated, screen-reader friendly, label name for the swatch.
     */
    labelName: {
      type: String,
      default: null,
    },

    /**
     * The translated label for the swatch when it is unavailable.
     */
    unavailableLabel: {
      type: String,
      default: 'unavailable',
    },

    /**
     * The first color of a swatch.
     * If secondColor is not provided this color will fill the entire swatch.
     */
    firstColor: {
      type: String,
      default: null,
    },

    /**
     * The second color of a swatch.
     * If provided then this color will fill the bottom half of the swatch
     * and firstColor will fill the upper half.
     */
    secondColor: {
      type: String,
      default: null,
    },

    /**
     * The image that will fill the swatch.
     * If provided then the image will override the color names provided if any.
     * Ensure that the image is appropriately sized before passing it into the swatch.
     * For reference, the default swatch is 1.875 rem (~24x24px).
     */
    imageUrl: {
      type: String,
      default: null,
    },

    /**
     * Displays a smaller swatch.
     */
    isSmall: {
      type: Boolean,
      required: false,
      default: false,
    },

    /**
     * Displays a larger swatch.
     */
    isLarge: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Displays an unavailable swatch.
     */
    isAvailable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  computed: {
    swatchClasses() {
      return {
        'pdl-swatch--small': this.isSmall,
        'pdl-swatch--large': this.isLarge && !this.isSmall,
        'pdl-swatch--unavailable': !this.isAvailable,
      };
    },
    figcaptionText() {
      if (this.labelName) {
        return this.labelName;
      }
      if (this.name) return this.name;
      if (this.firstColor && this.secondColor) return `${this.firstColor}-${this.secondColor}`;
      if (this.firstColor) return this.firstColor;
    },
  },

  watch: {
    imageUrl: {
      immediate: true,
      handler() {
        if (this.imageUrl && !this.name) {
          throw new TypeError('Name is required when using the imageURL prop.');
        }
      },
    },
  },
};
</script>

<style lang="scss">
@import 'Swatch.scss';
</style>
